import { Injectable } from '@angular/core';
import {
	Data$,
	RemoteData,
	appEntities,
	appEntity,
	catchErrorTo,
	switchTap,
} from '@lycoris-nubila/ngx-lycoris';
import { Observable, from, of } from 'rxjs';
import {
	catchError,
	map,
	mapTo,
	startWith,
	switchMap,
	switchMapTo,
	tap,
} from 'rxjs/operators';

import { MoveJobOfferToColumnCmd } from 'job/command/move-job-offer-to-column.cmd';
import { SaveJobOfferCmd } from 'job/command/save-job-offer.cmd';
import { UpdateJobOfferDocumentCmd } from 'job/command/update-job-offer-document.cmd';
import { IJobOffer, JobOffer } from 'job/job-offer';
import { JobOfferDocumentOutput } from 'job/model/job-offer-document.output';
import { JobOfferOutput } from 'job/model/job-offer.output';
import { JobOfferApiPort } from 'job/port/job-offer-api.port';
import { JobOfferApplicationPort } from 'job/port/job-offer-application.port';
import { JobOfferConverterPort } from 'job/port/job-offer-converter.port';
import { JobOfferRepositoryPort } from 'job/port/job-offer-repository.port';
import { ObserveJobOffersByOwnerIdAndOrganizationModuleIdQuery } from 'job/query/observe-job-offers-by-owner-id-and-organization-module-id.query';

@Injectable()
export class JobOfferApplicationAdapter implements JobOfferApplicationPort {
	constructor(
		private _jobOfferApiPort: JobOfferApiPort,
		private _jobOfferRepositoryPort: JobOfferRepositoryPort,
		private _jobOfferConverterPort: JobOfferConverterPort
	) {}

	public updateJobOfferDocument(
		cmd: UpdateJobOfferDocumentCmd
	): Observable<IJobOffer> {
		return this._jobOfferApiPort.updateJobOfferDocument(cmd).pipe(
			map((jobOffer) => this._jobOfferConverterPort.jobOfferAsEntity(jobOffer)),
			tap((jobOffer: JobOffer) =>
				this._jobOfferRepositoryPort.saveJobOffers([jobOffer])
			)
		);
	}

	public generateJobOfferDocumentUrl(
		file: File
	): Observable<JobOfferDocumentOutput> {
		return this._jobOfferApiPort.generateModuleDocumentUrl(file);
	}

	public observeJobOfferById(id: string): Data$<IJobOffer> {
		const api$ = from(this._jobOfferApiPort.fetchJobOfferById(id)).pipe(
			tap((jobOffer) =>
				this._jobOfferRepositoryPort.saveJobOffers([
					this._jobOfferConverterPort.jobOfferAsEntity(jobOffer),
				])
			),
			map(
				(jobOffer) =>
					({
						data: jobOffer,
						fresh: true,
					} as RemoteData<JobOfferOutput>)
			),
			catchError((_) =>
				of({ fresh: true, data: null } as RemoteData<JobOfferOutput>)
			),
			startWith({ fresh: false, data: null } as RemoteData<JobOfferOutput>)
		);

		const repo$ = this._jobOfferRepositoryPort.observeJobOfferById(id);

		return appEntity(api$, repo$);
	}

	public observeAttachedJobOffersByIds(ids: string[]): Observable<JobOffer[]> {
		const remote$ = from(this._jobOfferApiPort.fetchJobOffersByIds(ids)).pipe(
			switchTap((jobOffers) =>
				this._jobOfferRepositoryPort.saveJobOffers(
					this._jobOfferConverterPort.jobOffersAsEntities(jobOffers)
				)
			),
			switchMapTo(this._jobOfferRepositoryPort.observeJobOffersByIds(ids))
		);

		return this._jobOfferRepositoryPort
			.observeJobOffersByIds(ids)
			.pipe(
				switchMap((jobOffers) =>
					jobOffers.length === ids.length ? of(jobOffers) : remote$
				)
			);
	}

	public moveJobOfferToColumn(cmd: MoveJobOfferToColumnCmd): Observable<void> {
		return this._jobOfferApiPort.moveJobOfferToColumn(cmd).pipe(
			map((jobOffer) => this._jobOfferConverterPort.jobOfferAsEntity(jobOffer)),
			tap((jobOffer: JobOffer) =>
				this._jobOfferRepositoryPort.saveJobOffers([jobOffer])
			),
			mapTo(null)
		);
	}

	public saveJobOffer(cmd: SaveJobOfferCmd): Observable<void> {
		return (
			cmd.jobOfferId
				? this._jobOfferApiPort.updateJobOffer(cmd)
				: this._jobOfferApiPort.createJobOffer(cmd)
		).pipe(
			map((jobOffer) => this._jobOfferConverterPort.jobOfferAsEntity(jobOffer)),
			tap((jobOffer: JobOffer) =>
				this._jobOfferRepositoryPort.saveJobOffers([jobOffer])
			),
			mapTo(null)
		);
	}

	public observeJobOffersByOwnerIdAndOrganizationModuleId(
		query: ObserveJobOffersByOwnerIdAndOrganizationModuleIdQuery
	): Data$<IJobOffer[]> {
		const api$ = from(
			this._jobOfferApiPort.fetchJobOffersByOwnerIdAndOrganizationModuleId(query)
		).pipe(
			tap((jobOffers) =>
				this._jobOfferRepositoryPort.saveJobOffers(
					jobOffers.map((jobOffer) =>
						this._jobOfferConverterPort.jobOfferAsEntity(jobOffer)
					)
				)
			),
			map(
				(jobOffers) =>
					({
						data: jobOffers,
						fresh: true,
					} as RemoteData<JobOfferOutput[]>)
			),
			catchErrorTo({ fresh: true, data: [] }),
			startWith({ fresh: false, data: [] } as RemoteData<JobOfferOutput[]>)
		);

		const repo$ =
			this._jobOfferRepositoryPort.observeJobOffersByOwnerIdAndOrganizationModuleId(
				query
			);

		return appEntities(api$, repo$);
	}
}
